/**
 * Página de Aprobación de Clases - Para directores de asociación
 * Permite ver y aprobar/rechazar las clases progresivas que han sido enviadas para validación
 */

import { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Input,
} from "@/components/ui";
import {
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Loader2,
  User,
  Calendar,
  Building2,
  GraduationCap,
  ChevronDown,
  ChevronRight,
  X,
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { Can } from "@/components/auth/Can";
import { usePermissions } from "@/hooks/usePermissions";
import {
  usePendingApprovals,
  useApproveClass,
  useRejectClass,
} from "@/hooks/useProgress";
import { useClubsByAssociation } from "@/hooks/useOrganization";
import type { ClassProgress } from "@/types";

// Mapeo de imágenes de pins de clase
const classImageMap: Record<string, string> = {
  amigo: "/images/class/amigo.jpg",
  compañero: "/images/class/companero.jpg",
  companero: "/images/class/companero.jpg",
  explorador: "/images/class/explorador.jpg",
  guía: "/images/class/guia.jpg",
  guia: "/images/class/guia.jpg",
  orientador: "/images/class/orientador.jpg",
  viajero: "/images/class/viajero.jpg",
};

const getClassImage = (className: string): string | null => {
  const normalized = className
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return classImageMap[normalized] || null;
};

export default function ClassApprovalPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedClubs, setExpandedClubs] = useState<Set<string>>(new Set());
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const { profile } = useAuthStore();
  const { isAssociationAdmin, isUnionAdmin, isAdmin } = usePermissions();

  // Para admin_association, el scopeId es el ID de la asociación
  const associationId = profile?.scopeId || "";

  // Obtener clubes de la asociación
  const { data: clubs } = useClubsByAssociation(associationId);

  // Obtener clases pendientes de aprobación
  const {
    data: pendingClasses,
    isLoading,
    refetch,
  } = usePendingApprovals(associationId);

  // Mutations
  const approveMutation = useApproveClass();
  const rejectMutation = useRejectClass();

  // Verificar permisos
  const canApprove = isAssociationAdmin || isUnionAdmin || isAdmin;

  if (!canApprove) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="max-w-md border-0 shadow-lg">
          <CardContent className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/30">
              <XCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Acceso Denegado
            </h2>
            <p className="text-gray-600">
              No tienes permisos para aprobar clases progresivas. Esta función
              está disponible solo para directores de asociación.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Agrupar clases por club
  const classesByClub = (pendingClasses || []).reduce(
    (acc, cp) => {
      const clubId = cp.clubId;
      if (!acc[clubId]) {
        acc[clubId] = [];
      }
      acc[clubId].push(cp);
      return acc;
    },
    {} as Record<string, ClassProgress[]>,
  );

  // Filtrar por búsqueda
  const filteredClubIds = Object.keys(classesByClub).filter((clubId) => {
    const club = clubs?.find((c) => c.id === clubId);
    const clubName = club?.name || "";
    const classes = classesByClub[clubId];

    return (
      clubName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classes.some(
        (cp) =>
          cp.className.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cp.memberName?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    );
  });

  const toggleClub = (clubId: string) => {
    const newExpanded = new Set(expandedClubs);
    if (newExpanded.has(clubId)) {
      newExpanded.delete(clubId);
    } else {
      newExpanded.add(clubId);
    }
    setExpandedClubs(newExpanded);
  };

  const handleApprove = async (classProgressId: string) => {
    try {
      await approveMutation.mutateAsync(classProgressId);
      refetch();
    } catch (error) {
      console.error("Error al aprobar:", error);
    }
  };

  const handleReject = async () => {
    if (!rejectingId || !rejectionReason.trim()) return;

    try {
      await rejectMutation.mutateAsync({
        id: rejectingId,
        reason: rejectionReason.trim(),
      });
      setRejectingId(null);
      setRejectionReason("");
      refetch();
    } catch (error) {
      console.error("Error al rechazar:", error);
    }
  };

  const totalPending = pendingClasses?.length || 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            Aprobación de Clases Progresivas
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            Revisa y aprueba las clases completadas por los miembros
          </p>
        </div>
        <Badge
          variant="warning"
          className="self-start flex items-center gap-2 px-4 py-2 shadow-md"
        >
          <Clock className="w-4 h-4" />
          {totalPending} pendiente{totalPending !== 1 ? "s" : ""}
        </Badge>
      </div>

      {/* Búsqueda */}
      <Card className="border-0 shadow-md">
        <CardContent className="py-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Buscar por club, clase o miembro..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lista de clases pendientes */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : totalPending === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              ¡Todo al día!
            </h3>
            <p className="text-gray-500">
              No hay clases pendientes de aprobación en este momento.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredClubIds.map((clubId) => {
            const club = clubs?.find((c) => c.id === clubId);
            const classes = classesByClub[clubId];
            const isExpanded = expandedClubs.has(clubId);

            return (
              <Card key={clubId} className="border-0 shadow-lg overflow-hidden">
                <div
                  className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                  onClick={() => toggleClub(clubId)}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <CardTitle className="text-lg">
                          {club?.name || "Club desconocido"}
                        </CardTitle>
                        <Badge variant="warning">
                          {classes.length} pendiente
                          {classes.length !== 1 ? "s" : ""}
                        </Badge>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </CardHeader>
                </div>

                {isExpanded && (
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      {classes.map((cp) => {
                        const classImage = getClassImage(cp.className);
                        const submittedDate =
                          cp.submittedForApprovalAt?.toDate?.();

                        return (
                          <div
                            key={cp.id}
                            className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-100 hover:border-gray-200 transition-colors duration-200"
                          >
                            {/* Info de la clase */}
                            <div className="flex items-center gap-4 flex-1">
                              {classImage ? (
                                <img
                                  src={classImage}
                                  alt=""
                                  className="w-14 h-14 rounded-xl object-cover shadow-md"
                                />
                              ) : (
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-md shadow-green-500/25">
                                  <GraduationCap className="w-7 h-7 text-white" />
                                </div>
                              )}

                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-800">
                                  {cp.className}
                                </h4>
                                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mt-1">
                                  <span className="flex items-center gap-1">
                                    <User className="w-4 h-4" />
                                    {cp.memberName || "Miembro"}
                                  </span>
                                  {submittedDate && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      {submittedDate.toLocaleDateString("es", {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1">
                                  <Badge variant="success">
                                    {cp.completionPercentage}% completado
                                  </Badge>
                                </div>
                              </div>
                            </div>

                            {/* Acciones */}
                            <div className="flex gap-2 sm:flex-shrink-0">
                              <Can
                                permission="progress.approve"
                                clubId={cp.clubId}
                              >
                                <Button
                                  size="sm"
                                  onClick={() => handleApprove(cp.id!)}
                                  disabled={approveMutation.isPending}
                                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-md shadow-green-500/25 transition-all duration-300"
                                >
                                  {approveMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                  )}
                                  Aprobar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => setRejectingId(cp.id!)}
                                  disabled={rejectMutation.isPending}
                                  className="shadow-md shadow-red-500/25"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Rechazar
                                </Button>
                              </Can>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de rechazo */}
      {rejectingId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md border-0 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center shadow-md shadow-red-500/25">
                    <XCircle className="w-5 h-5 text-white" />
                  </div>
                  <CardTitle className="text-lg">Rechazar Clase</CardTitle>
                </div>
                <button
                  onClick={() => {
                    setRejectingId(null);
                    setRejectionReason("");
                  }}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 text-sm">
                Por favor, indica el motivo del rechazo para que el club pueda
                corregir los requisitos pendientes.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo del rechazo *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Ej: Falta documentación de la especialidad de Primeros Auxilios..."
                  className="w-full border-2 border-gray-200 rounded-xl p-3 text-sm min-h-[100px] focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-300"
                  autoFocus
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setRejectingId(null);
                    setRejectionReason("");
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="danger"
                  className="flex-1 shadow-md shadow-red-500/25"
                  onClick={handleReject}
                  disabled={!rejectionReason.trim() || rejectMutation.isPending}
                >
                  {rejectMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-2" />
                  )}
                  Confirmar Rechazo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
